import React, { useCallback, useEffect } from 'react';
import { BulkOperationsToolbarSkeleton } from '@atlassian/jira-bulk-operations-core/src/ui/toolbar/bulk-operations-toolbar-skeleton/index.tsx';
import { JiraEntryPointContainer } from '@atlassian/jira-entry-point-container/src/index.tsx';
import type { AnyEntryPoint } from '@atlassian/jira-entry-point/src/common/types.tsx';
import { useEntryPoint } from '@atlassian/jira-entry-point/src/controllers/use-entry-point/index.tsx';
import type { EntryPointReferenceSubject } from '@atlassian/jira-entry-point/src/controllers/utils/use-entry-point-load-manager/index.tsx';
import { useBulkOperationsActions } from '@atlassian/jira-bulk-operations-common/src/controllers/use-bulk-operations/index.tsx';
import { useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { FormSubmissionStatusContainer } from '@atlassian/jira-bulk-operations-common/src/controllers/use-form-submit-state/main.tsx';
import { EditFormSubmitStatus } from '@atlassian/jira-bulk-operations-common/src/ui/submit-status/index.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import { ToolbarWrapper as ToolbarWrapperOld } from '@atlassian/jira-floating-toolbar/src/ui/toolbar-wrapper/index.tsx';
// TODO: rename ToolbarWrapperNew to ToolbarWrapper when cleaning up migrate_to_platform_bulk_toolbar
import { ToolbarWrapper as ToolbarWrapperNew } from '@atlassian/platform-floating-toolbar';
import { asyncBulkOpsCoreEntryPoint } from '../entrypoint.tsx';
import type { BulkOpsCoreType } from './ui/index.tsx';

// to show at least one button
const MIN_TOOLBAR_WIDTH = 450;

const entryPointParams = {};

export const AsyncBulkOpsCore = <TEntryPoint extends AnyEntryPoint | undefined = undefined>(
	props: BulkOpsCoreType<TEntryPoint>,
): React.JSX.Element => {
	const { entryPointActions, entryPointReferenceSubject } = useEntryPoint(
		asyncBulkOpsCoreEntryPoint,
		entryPointParams,
	);

	// eslint-disable-next-line @atlassian/react-entrypoint/no-load-in-hooks
	useEffect(() => {
		entryPointActions.load();
	}, [entryPointActions]);

	return (
		<JiraEntryPointContainer
			id="jira.backlog.bulk.operations.core"
			packageName="jiraBulkOperationsCore"
			entryPointReferenceSubject={entryPointReferenceSubject}
			fallback={<></>}
			runtimeProps={props}
		/>
	);
};

type BulkOpsCoreWithToolbarProps<TEntryPoint extends AnyEntryPoint | undefined = undefined> =
	BulkOpsCoreType<TEntryPoint> & {
		bulkOpsCoreEntryPointReference: EntryPointReferenceSubject<typeof asyncBulkOpsCoreEntryPoint>;
	};

export const BulkOpsCoreWithToolbar = <TEntryPoint extends AnyEntryPoint | undefined = undefined>({
	bulkOpsCoreEntryPointReference,
	...props
}: BulkOpsCoreWithToolbarProps<TEntryPoint>): React.JSX.Element => {
	const hasSelectAllButton = Boolean(props.onSelectAll);
	const extraToolbarButtonsSkeleton =
		props.extraToolbarButtons?.map((button) => ({
			hasIcon: Boolean(button.icon),
			text: button.name,
		})) || [];

	const { dismissBulkOperation } = useBulkOperationsActions();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const onDeselectAll = useCallback(() => {
		dismissBulkOperation({
			createAnalyticsEvent,
			dismissType: 'DESELECT_ALL_ISSUES',
		});
	}, [createAnalyticsEvent, dismissBulkOperation]);

	// TODO: move this back to the bottom when cleaning up isLegacyBulkOperations
	const toolbarWrapper = fg('responsive_bulk_ops_toolbar_platform') ? (
		<ToolbarWrapperNew
			show={
				(props?.shouldSelectAllIssues &&
					expVal('bulk_ops_select_all_beyond_experiment', 'isEnabled', false)) ||
				props.selectedIssueIds.length > 0
			}
			minToolbarWidth={MIN_TOOLBAR_WIDTH}
			onDeselectAll={onDeselectAll}
		>
			{(maxToolbarWidth?: number) =>
				bulkOpsCoreEntryPointReference && (
					<JiraEntryPointContainer
						id="jiraAsyncBulkOperationsCoreWithToolBar"
						packageName="jiraBulkOperationsCoreWithToolBar"
						teamName="deliveroo"
						entryPointReferenceSubject={bulkOpsCoreEntryPointReference}
						fallback={
							<BulkOperationsToolbarSkeleton
								buttons={extraToolbarButtonsSkeleton}
								hasSelectAllButton={hasSelectAllButton}
							/>
						}
						runtimeProps={{ ...props, maxToolbarWidth }}
					/>
				)
			}
		</ToolbarWrapperNew>
	) : (
		<ToolbarWrapperOld
			show={
				(props?.shouldSelectAllIssues &&
					expVal('bulk_ops_select_all_beyond_experiment', 'isEnabled', false)) ||
				props.selectedIssueIds.length > 0
			}
			width="calc(100% - var(--leftSidebarWidth, 0px))"
			onDeselectAll={onDeselectAll}
		>
			{bulkOpsCoreEntryPointReference && (
				<JiraEntryPointContainer
					id="jiraAsyncBulkOperationsCoreWithToolBar"
					packageName="jiraBulkOperationsCoreWithToolBar"
					teamName="deliveroo"
					entryPointReferenceSubject={bulkOpsCoreEntryPointReference}
					fallback={
						<BulkOperationsToolbarSkeleton
							buttons={extraToolbarButtonsSkeleton}
							hasSelectAllButton={hasSelectAllButton}
						/>
					}
					runtimeProps={props}
				/>
			)}
		</ToolbarWrapperOld>
	);

	if (props.isLegacyBulkOperations) {
		return toolbarWrapper;
	}

	const formProps = {
		selectedIssueIds: props.selectedIssueIds,
		onOpenForm: props.onOpenForm,
		onCloseForm: props.onCloseForm,
		onUpdateError: props.onUpdateError,
		onUpdateSuccess: props.onUpdateSuccess,
		onLegacyBulkEditRequest: props.onLegacyBulkEditRequest,
		onUpdateStart: props.onUpdateStart,
	};

	return (
		<FormSubmissionStatusContainer isGlobal {...formProps}>
			{toolbarWrapper}
			<EditFormSubmitStatus />
		</FormSubmissionStatusContainer>
	);
};
